import React, { useState, useEffect, useRef } from "react"
import { useDispatch } from "react-redux"
import _get from "lodash/get"
import _isEmpty from "lodash/isEmpty"
import PropTypes from "prop-types"
import { NumericFormat } from "react-number-format"
import { isMobile, withOrientationChange } from "react-device-detect"

import { getConfig } from "@/constants/config"
import {
  cleanText,
  getShortenedUrl,
  sanitizeInnerHtml,
  sanitizeTextForAnalytics,
  validateField,
} from "@/utils/helper"
import { validateZip } from "@/utils/location"
import { addToCartItem } from "@/utils/addTocart"

import Button from "@/components/core/Button/Button"
import Input from "@/components/core/Input/Input"

import { dataLayerAddToCart } from "@/components/ProductDetail/v3/analytics"
import styles from "@/components/ProductDetail/v3/AddanInstallation/index.module.scss"
import { showPreviewCart, showToast } from "@/store/features/genericSlice"
import { addToCart } from "@/store/features/cartSlice"
import {
  addDataLayerOnCheckAvailability,
  getEventInfo,
} from "@/components/ProductDetail/v3/AddanInstallation/analytics"

const waitTimeForAddAnInstallationSectionToLoad = 500

const AddanInstallation = props => {
  const {
    key = "",
    staticTexts: {
      zipcodeEmptyError = "",
      validZipcode = "",
      eligibleZipCodeError = "",
      bathroomDesignService = "",
      yourCart = "",
      serviceItemsAdded = "",
      forText = "",
      change = "",
      zipCodeGuidText = "",
      percentOff = "",
      zipcode = "",
      search = "",
      eligibleZipCodeSuccess = "",
      ariaLabel: { serviceLearnMore = "", serviceAddToCart = "" } = {},
      addService = "",
    } = {},
    disableEcommerce = false,
    currencySign = "",
    currencyCode = "",
    locale = "",
    getZipCode = () => {},
    installtionAPIData = [],
    installationServiceData = {},
    pdpType = "",
    sku = "",
  } = props

  const dispatch = useDispatch()
  const addAnInstallationRef = useRef(null)
  const [isCheckAvailability, setIsCheckAvailability] = useState(true)
  const [userZipCode, setUserZipCode] = useState("")
  const [isNearest, setIsNearest] = useState(null)
  const [isNearestError, setIsNearestError] = useState(false)
  const [checkZipCode, setCheckZipCode] = useState("")
  const [zipCodeError, setZipCodeError] = useState({ show: false, message: "" })
  const [general, setGeneral] = useState({})
  const [cartUrl, setCartUrl] = useState("")
  const [installationServiceDataCtaLink, setInstallationServiceDataCtaLink] =
    useState("")

  useEffect(() => {
    getConfig().then(config => {
      const { general } = config
      setGeneral(general)
    })
  }, [])

  useEffect(() => {
    if (!_isEmpty(general)) {
      getShortenedUrl(general.cartLandingPagePath ?? "/").then(res =>
        setCartUrl(res)
      )
    }
  }, [general])

  useEffect(() => {
    const { ctaLink = "" } = installationServiceData
    if (ctaLink) {
      getShortenedUrl(ctaLink).then(url =>
        setInstallationServiceDataCtaLink(url)
      )
    }
  }, [installationServiceData.ctaLink])

  let percentDiscount
  let percentType
  const installServiceTitle = _get(installtionAPIData[0], `name[${locale}]`, "")
  const masterVariant = _get(installtionAPIData[0], "masterVariant", [])
  const masterVariantPrice = _get(masterVariant, "prices", [])
  const installationPrice = Number(
    _get(
      masterVariantPrice.find(item => item.value.currencyCode === currencyCode),
      "value.centAmount",
      0
    ) / 100
  ).toFixed(2)
  const isDiscount = _get(masterVariantPrice[0], "discounted", {})
  const installationDiscountedPrice = !_isEmpty(isDiscount)
    ? Number(
        _get(
          masterVariantPrice.find(
            item => item.discounted.value.currencyCode === currencyCode
          ),
          "discounted.value.centAmount",
          0
        ) / 100
      ).toFixed(2)
    : 0

  if (isDiscount?.discount?.obj?.value.type === "absolute") {
    percentDiscount =
      installationDiscountedPrice > 0 &&
      Number(
        _get(
          masterVariantPrice.find(
            item => item.discounted.value.currencyCode === currencyCode
          ),
          "discounted.discount.obj.value.money[0].centAmount",
          0
        ) / 100
      )
    percentType = "absolute"
  } else {
    percentDiscount =
      installationDiscountedPrice > 0 &&
      Number(
        _get(
          masterVariantPrice.find(
            item => item.discounted.value.currencyCode === currencyCode
          ),
          "discounted.discount.obj.value.permyriad",
          0
        ) / 100
      )
    percentType = "relative"
  }

  const designServicePrice = Number(
    _get(
      masterVariantPrice.find(item => item.value.currencyCode === currencyCode),
      "value.centAmount",
      0
    ) / 100
  ).toFixed(2)
  const designServiceDiscountedPrice = !_isEmpty(isDiscount)
    ? Number(
        _get(
          masterVariantPrice.find(
            item => item.discounted.value.currencyCode === currencyCode
          ),
          "discounted.value.centAmount",
          0
        ) / 100
      ).toFixed(2)
    : 0
  const masterVariantAttributes = _get(masterVariant, "attributes", [])
  const ProductWebFeatures = _get(
    masterVariantAttributes.find(attr => attr.name === "ProductWebFeatures"),
    "value",
    []
  )
  const customerFacingSKU = _get(
    masterVariantAttributes.find(attr => attr.name === "CustomerFacingSKU"),
    "value",
    []
  )
  const productBrand = _get(
    masterVariantAttributes.find(attr => attr.name === "CustomerFacingBrand"),
    "value",
    []
  )
  const discountPriceState =
    designServiceDiscountedPrice > 0
      ? "percentOff|" + percentDiscount
      : "regular price"

  let desc = ""
  ProductWebFeatures.slice(0, 3).forEach(item => {
    desc += item[locale] || ""
    if (desc && desc[desc.length - 1] !== ".") {
      desc += ". "
    }
  })

  useEffect(() => {
    setTimeout(() => {
      addDataLayerToFindAProLinkAddOnServices()
    }, waitTimeForAddAnInstallationSectionToLoad)
  }, [])

  const addDataLayerToFindAProLinkAddOnServices = () => {
    if (addAnInstallationRef.current) {
      const findAProAnchorLink = addAnInstallationRef.current.querySelector("a")
      if (findAProAnchorLink) {
        findAProAnchorLink.classList.add("gbh-data-layer")
        const eventInfo = JSON.stringify({
          eventAction: "pdp:add-on services:find a pro",
          eventMsg: "n/a",
          eventName: "pdp:add-on services:find a pro",
          eventStatus: "n/a",
          eventType: "navigation",
          internalLinkName: "find a pro",
          internalLinkPosition: "pdp",
          internalLinkType: "pdp:find a pro",
          internalLinkURL: findAProAnchorLink.getAttribute("href"),
          internalLinkZoneName: "pdp:add-on services",
        })
        findAProAnchorLink.setAttribute("data-gbh-data-layer", eventInfo)
        findAProAnchorLink.setAttribute(
          "data-gbh-data-layer-custom",
          getProductInfo()
        )
      }
    }
  }

  const handleCheckAvailability = async () => {
    setZipCodeError({ show: false, message: "" })
    let valid = true
    let errMsg = ""

    if (_isEmpty(checkZipCode)) {
      errMsg = zipcodeEmptyError
      setZipCodeError({ show: true, message: errMsg })
      valid = false
    } else if (!validateField("zipcode", checkZipCode)) {
      errMsg = validZipcode
      setZipCodeError({ show: true, message: errMsg })
      valid = false
    } else if (!(await validateZip(checkZipCode))) {
      errMsg = validZipcode
      setZipCodeError({ show: true, message: errMsg })
      valid = false
    }
    if (valid) {
      getZipCode(checkZipCode, handleZipCodeCallBack)
    } else {
      addDataLayerOnCheckAvailability(
        errMsg,
        installationServiceData?.sectionHeading || "",
        checkZipCode,
        productInfo
      )
    }
  }

  const handleZipCodeCallBack = isServiceAvailable => {
    setIsNearest(isServiceAvailable)
    setIsCheckAvailability(!isServiceAvailable)
    setIsNearestError(!isServiceAvailable)
    setUserZipCode(isServiceAvailable ? checkZipCode : "")
    const textMsg = isServiceAvailable
      ? null
      : cleanText(installationServiceData?.errorMessage ?? eligibleZipCodeError)
    const plainTextMsg = textMsg ? sanitizeTextForAnalytics(textMsg) : ""
    addDataLayerOnCheckAvailability(
      plainTextMsg,
      installationServiceData?.sectionHeading || "",
      checkZipCode,
      productInfo
    )
    if (isServiceAvailable) {
      sessionStorage.setItem("zipCodeError", "")
      sessionStorage.setItem("verifiedZip", checkZipCode)
    } else {
      sessionStorage.setItem("verifiedZip", "")
      sessionStorage.setItem("zipCodeError", checkZipCode)
    }
    setTimeout(() => {
      document
        .querySelector(".product-detail-page__right-sticky")
        .scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "nearest",
        })
    }, 100)
  }

  const beforeUnLoad = () => {
    sessionStorage.setItem("zipCodeError", "")
    sessionStorage.setItem("onChangeZip", "")
  }

  useEffect(() => {
    window.addEventListener("beforeunload", beforeUnLoad)

    return () => {
      window.removeEventListener("beforeunload", beforeUnLoad)
    }
  }, [])

  useEffect(() => {
    const isVerifiedZip = sessionStorage.getItem("verifiedZip")
    const zipCodeError = sessionStorage.getItem("zipCodeError")
    const onChangeZip = sessionStorage.getItem("onChangeZip")

    if (isVerifiedZip) {
      setIsNearest(true)
      setIsCheckAvailability(false)
      setIsNearestError(false)
      setUserZipCode(isVerifiedZip)
    } else if (zipCodeError) {
      setIsNearest(false)
      setIsCheckAvailability(true)
      setIsNearestError(true)
      setCheckZipCode(zipCodeError)
    }
    setCheckZipCode(onChangeZip)
  }, [])

  const handleClickChange = () => {
    setIsCheckAvailability(true)
    setIsNearest(false)
    setUserZipCode("")
    setCheckZipCode("")
  }

  const handleChangeZipCode = e => {
    const zipCodeVal = e.target.value
    const regExp = /^[0-9\b]+$/
    setIsNearestError(false)
    if (regExp.test(zipCodeVal) || zipCodeVal === "") {
      sessionStorage.setItem("onChangeZip", zipCodeVal)
      sessionStorage.setItem("verifiedZip", "")
      setCheckZipCode(zipCodeVal)
    } else if (!zipCodeVal || validateField("zipcode", zipCodeVal)) {
      setZipCodeError({ show: false, message: "" })
    }
  }

  const description = sanitizeTextForAnalytics(desc)?.toLowerCase()
  const serviceProductName = sanitizeTextForAnalytics(bathroomDesignService)
  const globalPromotionPrice =
    designServiceDiscountedPrice > 0
      ? designServiceDiscountedPrice
      : designServicePrice

  const productInfo = {
    productBrand: general?.siteName?.toLowerCase(),
    description: description || "n/a",
    frequency: "n/a",
    globalPromotionPrice: globalPromotionPrice || "n/a",
    isRecommended: "n/a",
    isSendNow: "n/a",
    isSubscription: "n/a",
    priceState: discountPriceState,
    productBasePrice: designServicePrice || "n/a",
    productCategory: "install service",
    productColor: "n/a",
    productCoupondiscount: "n/a",
    productID: customerFacingSKU || "n/a",
    productName: serviceProductName || "n/a",
    productPartnerBuyNow: "n/a",
    productRoom: "install service",
    productSalePrice: globalPromotionPrice,
    productSaleable: "true",
    productStatus: "in stock",
    productSuperSku: false,
    productTileName: serviceProductName || "n/a",
    quantity: 1,
    defaultImageName: "n/a",
    ratings: "n/a",
    numberOfReviews: "n/a",
    pdpType: pdpType,
    productFindingMethod: "n/a",
    productCollectionsName: "install service",
  }

  const handleAddToCartCallBack = res => {
    dataLayerAddToCart(
      { res, productInfo },
      installationServiceData.sectionHeading
    )
  }

  const handleAddToCart = () => {
    const item = {
      sku: sku,
      quantity: 1,
      userZipCode: userZipCode,
      brand: productBrand,
    }
    const { quantity } = item
    const itemCountMessage = `${quantity} ${serviceItemsAdded} `
    const ToasterMsg = `${itemCountMessage}<a href=${cartUrl}>${yourCart}</a>.`
    addToCartItem([
      {
        ...item,
        isBundle: false,
        backorder: false,
      },
    ]).then(response => {
      const body = {
        actions: response,
      }
      dispatch(addToCart(body))
        .unwrap()
        .then(res => {
          if (res) {
            handleAddToCartCallBack(res)
            if (isMobile) {
              dispatch(
                showToast({
                  message: ToasterMsg,
                  isVisible: true,
                })
              )
            } else {
              dispatch(showPreviewCart())
            }
          }
        })
    })
  }

  const getProductInfo = () => {
    return JSON.stringify({
      productInfo,
    })
  }

  return (
    <div className={styles.addAnInstallationWrapper}>
      <div className="row no-gutters product-detail-page__installation">
        <div className="pdp-add-an-installation col-12">
          <div className={`pdp-add-an-installation__left title`}>
            <h2 className="pdp-add-an-installation__title">
              {installationServiceData.sectionHeading !== null
                ? installationServiceData.sectionHeading
                : installServiceTitle}
              {isNearest ? (
                <>
                  {` ${forText}  ${userZipCode}`}
                  <p
                    role="button"
                    tabIndex="0"
                    className="pdp-add-an-installation__eligible-service-edit gbh-data-layer"
                    data-gbh-data-layer={getEventInfo(
                      "edit",
                      "",
                      installationServiceData?.sectionHeading || ""
                    )}
                    data-gbh-data-layer-custom={getProductInfo()}
                    onClick={handleClickChange}
                  >
                    {` ${change}`}
                  </p>
                </>
              ) : null}
            </h2>
            {!isNearest ? <p className="guidText">{zipCodeGuidText}</p> : null}
          </div>
          {isNearest ? (
            <div className="pdp-add-an-installation__right">
              <div className="pdp-add-an-installation__price">
                {installationPrice && (
                  <div className="pdp-add-an-installation__original-price">
                    <NumericFormat
                      value={
                        installationDiscountedPrice > 0
                          ? installationDiscountedPrice
                          : installationPrice
                      }
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={currencySign}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  </div>
                )}
                {installationDiscountedPrice > 0 && (
                  <div className="pdp-add-an-installation__discount-price">
                    <p className="pdp-add-an-installation__percent-discount">
                      {percentType === "absolute"
                        ? currencySign + percentDiscount
                        : percentDiscount + "%"}
                      {` ${percentOff}`}
                    </p>
                    <NumericFormat
                      value={installationPrice}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={currencySign}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : null}

          <div className={"pdp-add-an-installation__left"}>
            {isCheckAvailability ? (
              <React.Fragment>
                <div className="pdp-add-an-installation__zipcode-availability">
                  <Input
                    id={`zipcode_${key}`}
                    type="text"
                    maxLength={5}
                    ariaRequried={true}
                    placeholder={`${zipcode}`}
                    label={`${zipcode}`}
                    value={checkZipCode}
                    onKeyDown={e =>
                      e.key === "Enter" && handleCheckAvailability()
                    }
                    customClass={`${
                      zipCodeError.show
                        ? "pdp-add-an-installation__zipcode-input-error"
                        : ""
                    }`}
                    onChange={e => handleChangeZipCode(e)}
                  />
                  {checkZipCode ? (
                    <Button
                      role="button"
                      label={search}
                      size="small ghost"
                      type="primary"
                      onClick={handleCheckAvailability}
                      disabled={checkZipCode.length < 5}
                      useDefaultClass
                    />
                  ) : null}
                </div>
                {zipCodeError.show ? (
                  <div className="zipcode-error">{zipCodeError.message}</div>
                ) : null}
              </React.Fragment>
            ) : isNearest ? (
              <div
                role="alert"
                aria-live="polite"
                className="pdp-add-an-installation__eligible-service-message"
                ref={addAnInstallationRef}
              >
                {sanitizeInnerHtml(eligibleZipCodeSuccess)}
              </div>
            ) : null}
            {isNearestError ? (
              <div className="zipcode-error">
                {sanitizeInnerHtml(eligibleZipCodeError)}
              </div>
            ) : null}
          </div>
          <div className={"pdp-add-an-installation__right buttonGroup"}>
            <div className="pdp-add-an-installation__button-tab">
              {!isNearestError ? (
                <a
                  href={installationServiceDataCtaLink}
                  className="pdp-add-an-installation__learn-more gbh-data-layer"
                  target={installationServiceData.newTab ? "_blank" : "_self"}
                  aria-label={serviceLearnMore}
                  data-gbh-data-layer={getEventInfo(
                    "learn more",
                    installationServiceDataCtaLink ?? "/",
                    installationServiceData?.sectionHeading || ""
                  )}
                  data-gbh-data-layer-custom={getProductInfo()}
                  rel="noreferrer"
                >
                  {installationServiceData.ctaLabel}
                </a>
              ) : null}
              {isNearest && !disableEcommerce ? (
                <Button
                  role="button"
                  ariaLabel={serviceAddToCart}
                  label={addService}
                  size="small"
                  type="primary"
                  disabled={!isNearest}
                  onClick={handleAddToCart}
                  useDefaultClass
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

AddanInstallation.defaultProps = {
  currencySign: "",
  price: "",
  staticTexts: {
    learnMore: "",
    addToCart: "",
  },
}

AddanInstallation.propTypes = {
  currencySign: PropTypes.string.isRequired,
  price: PropTypes.string.isRequired,
  staticTexts: PropTypes.shape({
    learnMore: PropTypes.string.isRequired,
    addToCart: PropTypes.string.isRequired,
  }),
}

export default withOrientationChange(AddanInstallation)
